import { Location } from "gen/company/models_pb"
import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"
import { TriangleAlertIcon } from "lucide-react"
import { Format, Timestamp as TimestampTS } from "shared-ts/classes/timestamp"
import { UUID } from "shared-ts/classes/uuid"
import {
  Button,
  ButtonMode,
  ButtonSize,
} from "shared-web/components/base/control/Button"
import { CheckboxLabelled } from "shared-web/components/base/control/CheckboxLabelled"
import { BannerV2 } from "shared-web/components/base/informational/Banner"
import { Chip } from "shared-web/components/base/informational/Chip"
import { defModalId, Modal } from "shared-web/components/base/overlay/Modal"
import { useModal } from "shared-web/components/base/overlay/Modal/ModalContext"

import { useAppDispatch, useAppSelector } from "app/hooks"
import { DatePicker } from "components/composite/DatePicker"
import {
  PopoverSelect,
  useLocationsPopoverSelect,
} from "components/composite/popover"
import { useGetCompanyQuery } from "features/api/company"
import { useFlags } from "features/api/flags"
import { selectLocation as selectSignedInLocation } from "features/auth/authSlice"
import { useDeprecatedDeliveryExperienceFlag } from "hooks/flags"
import { FormErrors, useForm } from "hooks/useForm"

import {
  selectFulfillment as selectReturnFulfillment,
  setFulfillmentLocation as setReturnFulfillmentLocation,
} from "./returnsSlice"
import {
  selectFulfillment as selectSaleFulfillment,
  setFulfillmentLocation as setSaleFulfillmentLocation,
} from "./salesSlice"

export const fulfillmentLocationModalId = defModalId("fulfillmentLocationModal")

export type Fulfillment = {
  location: Location | null
  isDelivery: boolean
  deliveryDate: Date | null
}

export type StrictFulfillment = Omit<Fulfillment, "location"> & {
  location: Location
}

interface UseFulfillmentLocationResult {
  fulfillment: Fulfillment
  handleSubmit: (fulfillment: StrictFulfillment) => void
}

export function useSaleFulfillmentLocation(): UseFulfillmentLocationResult {
  // Global
  const { FULFILLMENT_LOCATION } = useFlags()
  const signedInLocation = useAppSelector(selectSignedInLocation)
  const dispatch = useAppDispatch()
  // Sale
  const saleFulfillment = useAppSelector(selectSaleFulfillment)
  return {
    fulfillment: {
      location:
        FULFILLMENT_LOCATION.boolVal && saleFulfillment?.location
          ? saleFulfillment?.location
          : signedInLocation,
      isDelivery: Boolean(saleFulfillment?.isDelivery),
      deliveryDate: saleFulfillment?.deliveryDate ?? null,
    },
    handleSubmit: (fulfillment: StrictFulfillment) => {
      dispatch(setSaleFulfillmentLocation(fulfillment))
    },
  }
}

export function useReturnFulfillmentLocation(): UseFulfillmentLocationResult {
  // Global
  const { FULFILLMENT_LOCATION } = useFlags()
  const signedInLocation = useAppSelector(selectSignedInLocation)
  const dispatch = useAppDispatch()
  // Return
  const returnFulfillment = useAppSelector(selectReturnFulfillment)

  return {
    fulfillment: {
      location:
        FULFILLMENT_LOCATION.boolVal && returnFulfillment?.location
          ? returnFulfillment?.location
          : signedInLocation,
      isDelivery: Boolean(returnFulfillment?.isDelivery),
      deliveryDate: returnFulfillment?.deliveryDate ?? null,
    },
    handleSubmit: (fulfillment: StrictFulfillment) => {
      dispatch(setReturnFulfillmentLocation({ fulfillment }))
    },
  }
}

interface FulFillmentLocationModalButtonProps {
  fulfillment: Fulfillment
  handleSubmit: (fulfillment: StrictFulfillment) => void
}
export function FulFillmentLocationModalButton({
  fulfillment,
  handleSubmit,
}: FulFillmentLocationModalButtonProps) {
  const { FULFILLMENT_LOCATION } = useFlags()
  const { data: companyData } = useGetCompanyQuery({})
  const signedInLocation = useAppSelector(selectSignedInLocation)
  const fulfillmentLocationModal = useModal(fulfillmentLocationModalId)

  if (!FULFILLMENT_LOCATION.boolVal) {
    return null
  }
  if (!companyData?.company?.allowDifferentFulfillmentLocation) {
    return null
  }
  if (!fulfillment.location) {
    return null
  }
  if (!signedInLocation) {
    return null
  }

  const warningMessageText = warningMessage(
    signedInLocation,
    fulfillment.location
  )
  return (
    <div className="flex flex-row items-center gap-x-2">
      <div className="flex flex-row items-center gap-x-1">
        <div className="text-body color-default font-medium">
          Fulfillment Location:
        </div>
        <Chip color={"gray"} text={fulfillment.location.prefix} />
        <span className="text-xs">{fulfillmentText(fulfillment)}</span>
      </div>
      <Button
        mode={ButtonMode.Secondary}
        onClick={fulfillmentLocationModal.open}
        size={ButtonSize.Small}
      >
        edit
      </Button>
      {warningMessageText && (
        <div className="color-warning flex flex-row items-center gap-x-1 text-xs">
          <TriangleAlertIcon />
          <div>{warningMessageText}</div>
        </div>
      )}
      <FulfillmentLocationModal
        fulfillment={{
          location: fulfillment.location,
          isDelivery: fulfillment.isDelivery,
          deliveryDate: fulfillment.deliveryDate,
        }}
        signedInLocation={signedInLocation}
        handleSubmit={handleSubmit}
      />
    </div>
  )
}
export function fulfillmentText(fulfillment: Fulfillment | undefined) {
  if (!fulfillment?.location?.name) {
    return ""
  }
  const name = fulfillment.location.name
  if (!fulfillment.isDelivery) {
    return name
  }
  if (!fulfillment.deliveryDate) {
    return `${name} (delivery)`
  }
  const deliveryDate = TimestampPB.fromDate(fulfillment.deliveryDate)
  return `${name} (delivery ${TimestampTS.format(Format.DATE_SHORT, deliveryDate)})`
}

interface FulfillmentLocationModalContentProps {
  fulfillment: StrictFulfillment
  signedInLocation: Location
  handleSubmit: (fulfillment: StrictFulfillment) => void
}

export function FulfillmentLocationModal(
  props: FulfillmentLocationModalContentProps
) {
  return (
    <Modal id={fulfillmentLocationModalId} size="sm" scrollable={false}>
      <FulfillmentLocationModalContent {...props} />
    </Modal>
  )
}

function FulfillmentLocationModalContent({
  fulfillment,
  signedInLocation,
  handleSubmit,
}: FulfillmentLocationModalContentProps) {
  const { close } = useModal(fulfillmentLocationModalId)
  const { isDeprecatedDeliveryExperienceEnabled } =
    useDeprecatedDeliveryExperienceFlag()
  const {
    values,
    setValue,
    handleSubmit: handleSubmitForm,
    isEdited,
    isError,
    errors,
  } = useForm<StrictFulfillment>({
    defaultValues: {
      ...fulfillment,
    },
    eq: isFulfillmentEqual,
    validate: (values) => {
      const errors: FormErrors<StrictFulfillment> = {}
      if (!values.location) {
        errors.location = "Location is required"
      }
      if (
        isDeprecatedDeliveryExperienceEnabled &&
        values.isDelivery &&
        !values.deliveryDate
      ) {
        errors.isDelivery = "Delivery date is required"
      }
      return errors
    },
  })
  function handleSelectLocation(location: Location | null) {
    if (!location) {
      return
    }
    setValue("location", location)
  }

  const { popoverSelectProps } = useLocationsPopoverSelect({
    selected: values.location,
    onSelect: handleSelectLocation,
  })

  function handleSelectIsDelivery(isDelivery: boolean) {
    setValue("isDelivery", isDelivery)
    if (!isDelivery) {
      setValue("deliveryDate", null)
    }
  }

  function onSubmit(fulfillment: StrictFulfillment) {
    handleSubmit(fulfillment)
    close()
  }

  const warningMessageText = warningMessage(
    signedInLocation,
    values.location ?? null
  )

  return (
    <form
      onSubmit={handleSubmitForm(onSubmit)}
      className="flex flex-col gap-y-4"
    >
      <h2>Edit fulfillment location</h2>
      {isError && (
        <BannerV2 severity="error" message="Please fix the errors below" />
      )}
      {warningMessageText && (
        <BannerV2 severity="warning" message={warningMessageText} />
      )}
      <PopoverSelect
        {...popoverSelectProps}
        label="Fulfillment location"
        placeholder="Select location"
        errorMessage={errors.location}
      />
      {isDeprecatedDeliveryExperienceEnabled && (
        <>
          <CheckboxLabelled
            checked={values.isDelivery}
            onChange={handleSelectIsDelivery}
            errorMessage={errors.isDelivery}
          >
            Is delivery
          </CheckboxLabelled>
          {values.isDelivery && (
            <DatePicker
              placeholder="Delivery date"
              pickedDate={values.deliveryDate ?? null}
              onPickDate={(date) => setValue("deliveryDate", date)}
            />
          )}
        </>
      )}
      <div className="flex flex-row justify-end gap-4">
        <Button mode="secondary" onClick={close}>
          Cancel
        </Button>
        <Button mode="primary" type="submit" disabled={!isEdited}>
          Save
        </Button>
      </div>
    </form>
  )
}

export function isFulfillmentEqual(a?: Fulfillment, b?: Fulfillment) {
  return (
    UUID.eqFromPB(a?.location?.id, b?.location?.id) &&
    a?.isDelivery === b?.isDelivery &&
    TimestampTS.eqFromDate(a?.deliveryDate, b?.deliveryDate)
  )
}

const warningMessage = (
  signedInLocation: Location,
  fulfillmentLocation: Location
) => {
  if (UUID.eqFromPB(signedInLocation.id, fulfillmentLocation.id)) {
    return undefined
  }
  return "The selected location is different from your current location"
}
